import { RecentlyViewed } from '@theme/recently-viewed-products';

class RecentlyViewedProducts extends HTMLElement {
  #abortController = null;
  #intersectionObserver = null;
  #resizeObserver = null;
  #scrollFrame = 0;

  connectedCallback() {
    const products = this.#getVisibleProducts();
    if (products.length === 0) {
      this.#hide();
      return;
    }

    this.hidden = false;
    this.dataset.hasHistory = 'true';
    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        this.#load(products);
      },
      { rootMargin: '500px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#abortController?.abort();
    cancelAnimationFrame(this.#scrollFrame);
  }

  #getVisibleProducts() {
    const currentProductId = this.dataset.currentProductId;
    const collectionHandle = this.dataset.collectionHandle;
    const excludeCurrent = this.dataset.excludeCurrent === 'true';
    const requestLimit = collectionHandle ? RecentlyViewed.MAX_PRODUCTS : this.#getMaxProducts();

    return RecentlyViewed.getProducts()
      .filter((product) => !excludeCurrent || product.id !== currentProductId)
      .slice(0, requestLimit);
  }

  #getMaxProducts() {
    return Math.min(Math.max(Number(this.dataset.maxProducts) || 8, 1), RecentlyViewed.MAX_PRODUCTS);
  }

  /**
   * @param {ReturnType<typeof RecentlyViewed.getProducts>} products
   */
  async #load(products) {
    const endpoint = this.dataset.endpoint;
    const track = this.querySelector('[data-recently-viewed-track]');
    if (!endpoint || !(track instanceof HTMLElement)) {
      this.#hide();
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('type', 'product');
      url.searchParams.set('q', products.map((product) => `id:${product.id}`).join(' OR '));

      const response = await fetch(url, {
        signal: this.#abortController.signal,
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Recently viewed request failed: ${response.status}`);

      const template = document.createElement('template');
      template.innerHTML = await response.text();
      const rendered = template.content.querySelector('[data-recently-viewed-rendered]');
      const collectionHandle = this.dataset.collectionHandle;
      let cards = rendered ? [...rendered.children] : [];

      if (collectionHandle) {
        cards = cards.filter((card) => {
          try {
            return JSON.parse(card.getAttribute('data-collection-handles') || '[]').includes(collectionHandle);
          } catch {
            return false;
          }
        });
      }
      cards = cards.slice(0, this.#getMaxProducts());

      if (cards.length === 0) {
        this.#hide();
        return;
      }

      for (const image of rendered.querySelectorAll('img')) {
        image.loading = 'lazy';
        image.fetchPriority = 'auto';
      }

      await Promise.all([
        import('@theme/product-card'),
        import('@theme/product-form'),
        import('@theme/product-price'),
      ]);

      track.replaceChildren(...cards);
      track.hidden = false;
      this.querySelector('[data-recently-viewed-skeleton]')?.setAttribute('hidden', '');
      this.setAttribute('aria-busy', 'false');
      this.#setupControls(track);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#hide();
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLElement} track
   */
  #setupControls(track) {
    const controls = this.querySelector('[data-recently-viewed-controls]');
    const previous = this.querySelector('[data-recently-viewed-previous]');
    const next = this.querySelector('[data-recently-viewed-next]');
    if (
      !(controls instanceof HTMLElement) ||
      !(previous instanceof HTMLButtonElement) ||
      !(next instanceof HTMLButtonElement)
    ) {
      return;
    }

    previous.addEventListener('click', () => this.#scroll(track, -1));
    next.addEventListener('click', () => this.#scroll(track, 1));
    track.addEventListener('scroll', () => this.#queueControlUpdate(track, controls, previous, next), {
      passive: true,
    });
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.#scroll(track, event.key === 'ArrowLeft' ? -1 : 1);
    });

    this.#resizeObserver = new ResizeObserver(() => this.#updateControls(track, controls, previous, next));
    this.#resizeObserver.observe(track);
    this.#updateControls(track, controls, previous, next);
  }

  /**
   * @param {HTMLElement} track
   * @param {number} direction
   */
  #scroll(track, direction) {
    const { cardWidth, gap, visibleCards } = this.#getTrackMetrics(track);

    track.scrollBy({
      left: direction * (cardWidth + gap) * visibleCards,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  /**
   * @param {HTMLElement} track
   */
  #getTrackMetrics(track) {
    const firstCard = track.firstElementChild;
    const styles = getComputedStyle(track);
    const cardWidth = firstCard instanceof HTMLElement ? firstCard.getBoundingClientRect().width : track.clientWidth;
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const visibleCards = Math.max(Math.round(track.clientWidth / (cardWidth + gap)), 1);

    return { cardWidth, gap, visibleCards };
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} controls
   * @param {HTMLButtonElement} previous
   * @param {HTMLButtonElement} next
   */
  #queueControlUpdate(track, controls, previous, next) {
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = requestAnimationFrame(() => this.#updateControls(track, controls, previous, next));
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} controls
   * @param {HTMLButtonElement} previous
   * @param {HTMLButtonElement} next
   */
  #updateControls(track, controls, previous, next) {
    const maximumScroll = Math.max(track.scrollWidth - track.clientWidth, 0);
    controls.hidden = maximumScroll <= 1;
    previous.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= maximumScroll - 1;
  }

  #hide() {
    this.hidden = true;
    this.removeAttribute('data-has-history');
    this.setAttribute('aria-busy', 'false');
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
